import { useEffect, useState } from "react";
import api from "../utils/axios";

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);

  const [form, setForm] = useState({
    payment_settings_id: 1,
    user_id: 1,
    payment_type: "upi",
    upi_id: "",
    wallet_address: "",
    bank_account_number: "",
    bank_name: "",
    ifsc_code: "",
    account_holder_name: "",
    qr_file: null, // ✅ IMAGE FILE
    currency: "INR",
  });

  /* ================= FETCH ================= */
  const loadMethods = async () => {
    const res = await api.get("/payment-methods");
    setMethods(res.data.data || []);
  };

  useEffect(() => {
    loadMethods();
  }, []);

  /* ================= FILE HANDLER ================= */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setForm({ ...form, qr_file: file });
    setPreview(URL.createObjectURL(file));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = new FormData();

    Object.keys(form).forEach((key) => {
      if (form[key] !== null) {
        payload.append(key, form[key]);
      }
    });

    if (editingId) {
      await api.put(`/payment-methods/${editingId}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } else {
      await api.post("/payment-methods", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    resetForm();
    loadMethods();
  };

  const resetForm = () => {
    setForm({
      payment_settings_id: 1,
      user_id: 1,
      payment_type: "upi",
      upi_id: "",
      wallet_address: "",
      bank_account_number: "",
      bank_name: "",
      ifsc_code: "",
      account_holder_name: "",
      qr_file: null,
      currency: "INR",
    });
    setPreview(null);
    setEditingId(null);
  };

  /* ================= EDIT ================= */
  const handleEdit = (m) => {
    setEditingId(m.id);
    setPreview(m.qr_image || null); // backend image url

    setForm({
      payment_settings_id: m.payment_settings_id,
      user_id: m.user_id,
      payment_type: m.payment_type,
      upi_id: m.upi_id || "",
      wallet_address: m.wallet_address || "",
      bank_account_number: m.bank_account_number || "",
      bank_name: m.bank_name || "",
      ifsc_code: m.ifsc_code || "",
      account_holder_name: m.account_holder_name || "",
      qr_file: null, // new upload optional
      currency: m.currency || "INR",
    });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payment method?")) return;
    await api.delete(`/payment-methods/${id}`);
    loadMethods();
  };

  /* ================= STATUS ================= */
  const toggleStatus = async (id, is_active) => {
    await api.patch(`/payment-methods/${id}/status`, { is_active });
    loadMethods();
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Payment Methods
        </h1>
        <p className="text-sm text-gray-500">
          Manage payment methods and settings for user transactions.
        </p>
      </div>

      {/* ================= FORM ================= */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          {editingId ? "Edit Payment Method" : "Add Payment Method"}
        </h2>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {/* TYPE */}
          <select
            className="border rounded-lg px-3 py-2"
            value={form.payment_type}
            onChange={(e) =>
              setForm({ ...form, payment_type: e.target.value })
            }
          >
            <option value="upi">UPI</option>
            <option value="crypto">Crypto</option>
            {/*<option value="bank">Bank</option>*/}
          </select>

          {/* UPI */}
          {form.payment_type === "upi" && (
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="UPI ID"
              value={form.upi_id}
              onChange={(e) =>
                setForm({ ...form, upi_id: e.target.value })
              }
            />
          )}

          {/* CRYPTO */}
          {form.payment_type === "crypto" && (
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="Wallet Address"
              value={form.wallet_address}
              onChange={(e) =>
                setForm({ ...form, wallet_address: e.target.value })
              }
            />
          )}

          {/* BANK */}
          {form.payment_type === "bank" && (
            <>
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="Account Holder Name"
                value={form.account_holder_name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    account_holder_name: e.target.value,
                  })
                }
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="Bank Name"
                value={form.bank_name}
                onChange={(e) =>
                  setForm({ ...form, bank_name: e.target.value })
                }
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="Account Number"
                value={form.bank_account_number}
                onChange={(e) =>
                  setForm({
                    ...form,
                    bank_account_number: e.target.value,
                  })
                }
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="IFSC Code"
                value={form.ifsc_code}
                onChange={(e) =>
                  setForm({ ...form, ifsc_code: e.target.value })
                }
              />
            </>
          )}

          {/* IMAGE UPLOAD */}
          {(form.payment_type === "upi" ||
            form.payment_type === "crypto") && (
            <div className="md:col-span-2">
              <label className="text-sm text-gray-500">
                Upload QR / Reference Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="mt-1"
              />

              {preview && (
                <img
                  src={preview}
                  alt="preview"
                  className="mt-3 h-32 rounded border"
                />
              )}
            </div>
          )}

          {/* ACTIONS */}
          <div className="md:col-span-2 flex gap-3 mt-4">
            <button
              type="submit"
              className="px-6 py-2 rounded-lg bg-gray-900 text-white"
            >
              {editingId ? "Update" : "Create"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 rounded-lg border"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ================= LIST ================= */}
<div className="bg-white border rounded-xl shadow-sm overflow-hidden">
  <table className="w-full text-sm">
    <thead className="bg-gray-50 text-gray-600">
      <tr>
        <th className="px-4 py-3 text-left">#</th>
        <th className="px-4 py-3 text-left">Type</th>
        <th className="px-4 py-3 text-left">Details</th>
        <th className="px-4 py-3 text-left">Image</th>
        <th className="px-4 py-3 text-left">Status</th>
        <th className="px-4 py-3 text-left">Actions</th>
      </tr>
    </thead>

    <tbody>
      {methods.length === 0 ? (
        <tr>
          <td colSpan={6} className="py-6 text-center text-gray-500">
            No payment methods found
          </td>
        </tr>
      ) : (
        methods.map((m, index) => (
          <tr key={m.id} className="border-t hover:bg-gray-50">
            <td className="px-4 py-3">{index + 1}</td>

            <td className="px-4 py-3 capitalize">
              {m.payment_type}
            </td>

            <td className="px-4 py-3 text-gray-600">
              {m.payment_type === "upi" && m.upi_id}
              {m.payment_type === "crypto" && m.wallet_address}
              {m.payment_type === "bank" &&
                `${m.bank_name} • ${m.account_holder_name}`}
            </td>

            <td className="px-4 py-3">
              {m.qr_image ? (
                <img
                  src={m.qr_image}
                  alt="qr"
                  className="h-10 rounded cursor-pointer"
                  onClick={() => window.open(m.qr_image)}
                />
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </td>

            <td className="px-4 py-3">
              <span
                className={`px-3 py-1 rounded-full text-xs ${
                  m.is_active
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {m.is_active ? "Active" : "Disabled"}
              </span>
            </td>

            <td className="px-4 py-3 flex gap-2">
              <button
                onClick={() => handleEdit(m)}
                className="px-3 py-1 rounded border text-sm"
              >
                Edit
              </button>

              <button
                onClick={() => toggleStatus(m.id, !m.is_active)}
                className="px-3 py-1 rounded border text-sm"
              >
                {m.is_active ? "Disable" : "Enable"}
              </button>

              <button
                onClick={() => handleDelete(m.id)}
                className="px-3 py-1 rounded bg-red-500 text-white text-sm"
              >
                Delete
              </button>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
</div>

    </div>
  );
}
